import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fetchAgents, RagAgent } from "../api/ragApi";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { openStandardModal, closeStandardModal } from "src/common/state/slice/modal/modalSlice";
import { SearchInput } from "src/common/components/SearchInput";
import WixarBadge from "./WixarBadge";

const AGENT_ICONS: Record<string, string> = {
  default: "\uD83E\uDD16",
  book: "\uD83D\uDCDA",
  support: "\uD83C\uDFA7",
  brain: "\uD83E\uDDE0",
  search: "\uD83D\uDD0D",
  chat: "\uD83D\uDCAC",
  star: "\u2B50",
  tool: "\uD83D\uDD27",
  idea: "\uD83D\uDCA1",
  language: "\uD83C\uDF0D",
  education: "\uD83C\uDF93",
  expert: "\uD83D\uDC54",
};

interface DocumentAgentAssignerProps {
  documentId: number;
  currentAgentIds: number[];
  onAssign: (docId: number, agentId: number) => Promise<void>;
  onUnassign: (docId: number, agentId: number) => Promise<void>;
  /** Render a custom trigger instead of the default text button */
  trigger?: (open: () => void) => React.ReactNode;
}

const DocumentAgentAssigner = ({
  documentId,
  currentAgentIds,
  onAssign,
  onUnassign,
  trigger,
}: DocumentAgentAssignerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [agents, setAgents] = useState<RagAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const modalId = `doc-agent-assigner-${documentId}`;

  useEffect(() => {
    fetchAgents()
      .then((data) => setAgents((Array.isArray(data) ? data : []).filter((a) => !a.is_shared)))
      .catch((err) => console.error("Failed to fetch agents:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (agentId: number) => {
    setToggling(agentId);
    try {
      if (currentAgentIds.includes(agentId)) {
        await onUnassign(documentId, agentId);
      } else {
        await onAssign(documentId, agentId);
      }
    } catch (err) {
      console.error("Failed to toggle agent:", err);
    }
    setToggling(null);
  };

  const openModal = () => dispatch(openStandardModal(modalId));

  const { assignedAgents, availableAgents } = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = q
      ? agents.filter(
          (a) =>
            a.name.toLowerCase().includes(q) ||
            (a.description && a.description.toLowerCase().includes(q)),
        )
      : agents;

    return {
      assignedAgents: filtered.filter((a) => currentAgentIds.includes(a.id)),
      availableAgents: filtered.filter((a) => !currentAgentIds.includes(a.id)),
    };
  }, [agents, currentAgentIds, search]);

  const renderAgentRow = (agent: RagAgent) => {
    const isAssigned = currentAgentIds.includes(agent.id);
    const isToggling = toggling === agent.id;
    return (
      <button
        key={agent.id}
        type="button"
        onClick={() => handleToggle(agent.id)}
        disabled={isToggling}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-all text-sm w-full ${
          isAssigned
            ? "border-green-400 bg-green-50"
            : "border-gray-200 bg-white hover:border-gray-300"
        } ${isToggling ? "opacity-50" : "cursor-pointer"}`}
      >
        <span className="text-lg flex-shrink-0">
          {AGENT_ICONS[agent.icon] || AGENT_ICONS.default}
        </span>
        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900 truncate text-sm flex items-center gap-1">
            {agent.name}
            {agent.is_shared && <WixarBadge size="sm" />}
          </span>
          {agent.description && (
            <span className="text-xs text-gray-500 truncate block">{agent.description}</span>
          )}
        </div>
        {(agent.document_count ?? 0) > 0 && (
          <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
            {agent.document_count} doc{(agent.document_count ?? 0) > 1 ? "s" : ""}
          </span>
        )}
        {isToggling && (
          <span className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
        )}
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
            isAssigned ? "border-green-500 bg-green-500" : "border-gray-300"
          }`}
        >
          {isAssigned && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </button>
    );
  };

  return (
    <>
      {trigger ? (
        trigger(openModal)
      ) : (
        <button
          onClick={openModal}
          className="text-xs text-green-600 hover:text-green-700 font-medium transition-colors"
        >
          {t("pages.agents.assignToAgent")}
        </button>
      )}

      <ModalStandard
        id={modalId}
        fullScreen={false}
        showCloseButton
        minimize
        className="w-full max-w-md h-fit"
      >
        <h3 className="font-semibold text-base text-gray-900 mb-3 pr-6">
          {t("pages.agents.assignedAgents")}
        </h3>

        {!loading && agents.length > 0 && (
          <div className="mb-2">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t("pages.agents.ragAgentSelect.searchPlaceholder")}
            />
            <p className="text-xs text-gray-400 mt-1.5 tabular-nums">
              {assignedAgents.length + availableAgents.length} / {agents.length}
              {search.trim() && ` — "${search.trim()}"`}
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : agents.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">
            {t("pages.agents.ragAgentSelect.noAgentsAvailable")}
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto">
            {assignedAgents.length > 0 && (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1">
                  {t("pages.agents.assignedAgents")} ({assignedAgents.length})
                </p>
                {assignedAgents.map(renderAgentRow)}
              </>
            )}

            {availableAgents.length > 0 && (
              <>
                {assignedAgents.length > 0 && <div className="border-t border-gray-200 my-1" />}
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1">
                  {t("general.available")} ({availableAgents.length})
                </p>
                {availableAgents.map(renderAgentRow)}
              </>
            )}

            {assignedAgents.length === 0 && availableAgents.length === 0 && (
              <p className="text-sm text-gray-400 py-2 text-center">
                {t("statsNew.emptyState.noSearchResults")}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end mt-4 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => dispatch(closeStandardModal(modalId))}
            className="btn-alternative-outline text-sm"
          >
            {t("general.close")}
          </button>
        </div>
      </ModalStandard>
    </>
  );
};

export default DocumentAgentAssigner;
